/**
 * @copyright Copyright 2026 by Radivision Inc., CA, USA. All Rights Reserved.
 * @description Seed source for the Pitch Planet carousel and grid shuffles.
 * @filename pitch-seed.ts
 */

import { Router } from "./router";

/**
 * Query-string parameter that pins the Pitch Planet order so QA can reproduce
 * a given arrangement, for example /pitchplanet?day=7
 */
export const PITCH_SEED_PARAM = "day";

/**
 * PseudoRandom is a Park-Miller generator modulo 2^31-1 and callers seed it with
 * (seed + 1) * 12356. A seed of 2147483646 makes (seed + 1) congruent to zero, which
 * pins the generator at zero forever and stops the list shuffling at all, so the
 * random range stops one below that value.
 */
const MAX_SEED = 2147483645;

/**
 * The seed drawn for this page load. Held at module scope so that every consumer
 * within one SPA session agrees on the order, and so re-renders, filter changes and
 * client-side navigation do not reshuffle the list under the visitor.
 */
let sessionSeed: number = null;

/**
 * Returns the seed for the Pitch Planet carousel and grid shuffles.
 *
 * An explicit `?day=N` wins, so a given URL always reproduces the same arrangement.
 * Any finite integer is accepted, zero included. Otherwise a fresh seed is drawn once
 * per page load, so different visitors, and the same visitor on a later visit, land on
 * a different company.
 *
 * @returns {number} A non-negative integer seed.
 */
export function getPitchSeed(): number {
  const RAW: string = Router.getParameterByName(PITCH_SEED_PARAM);
  const OVERRIDE: number = RAW === null || RAW === undefined || RAW === "" ? NaN : parseInt(RAW, 10);

  if (isFinite(OVERRIDE)) {
    return Math.abs(OVERRIDE) % (MAX_SEED + 1);
  }

  if (sessionSeed === null) {
    sessionSeed = 1 + Math.floor(Math.random() * MAX_SEED);
  }

  return sessionSeed;
}

/**
 * Clears the memoised per-load seed. Test-only hook; the app never calls this.
 */
export function resetPitchSeedForTests(): void {
  sessionSeed = null;
}
